import { E2eTestElementId } from '@upright/test-ids/dom-elements';
import { useAskAiEntryPoint } from '@upright/ui/chat/use-ask-ai-entry-point';
import { useOpenAskAiPanel } from '@upright/ui/chat/use-open-ask-ai-panel';
import UprightLogo from '@upright/ui/common/generic/components/icons/logo/logo-platform-short.svg';
import { UprightLogoSymbolBlackWhite } from '@upright/ui/common/generic/components/icons/logo/upright-logo-symbol-black-white';
import { MenuBars } from '@upright/ui/icons';
import FeedbackIcon from '@upright/ui/icons/img/streamline-mini-line/mail/chat-bubble/mail-chat-bubble-square.svg';
import AiSparklesIcon from '@upright/ui/icons/ai-sparkles-streamline-core';
import { useMainContext } from '@upright/ui/views/root/main/context';
import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { type SignedInPerson, UnifiedAccountMenu } from './unified-account-menu';
import { UnifiedHelpMenu } from './unified-help-menu';
import { UnifiedShellSearch } from './unified-shell-search';
import { useNavigationOrganizationId } from './profile-back-trail';
import { withProfileOrganization } from './profile-organization-context';
import styles from './unified-top-bar.module.css';

/**
 * The unified shell's top bar (PLT-6527): the design system's white 50px bar with the
 * investor side's controls in its four slots (design/40-shell-and-pages.md "Top bar").
 *
 *   brand    the wordmark, linking home
 *   context  empty — the organisation is named by the rail's selector, and the investor
 *            side has no workspace to switch; the corporate shell's pill is DMA-only
 *   actions  Ask AI, the global search, Feedback, Help
 *   account  the initials square, opening the account menu
 *
 * Ask AI leads the actions slot rather than following the search field, because it is the one
 * control in the cluster that comes and goes (see `AskAiLink`). At the leading edge its absence
 * shortens the group; between the search field and Help it would open a gap in the middle of
 * the cluster and shift the search input relative to the two controls that never move.
 *
 * What the dark `BouToolHeader` carried and where it went (PLT-6531): the search keeps its
 * place in the bar, as a standard input in the actions slot; the release and product-data
 * selectors move into the account menu; the Tools menu is retired, because its investor
 * destinations are rail rows and its corporate ones are not reachable from an investor
 * organisation. The old header's Contact, Knowledge base and About entries are the Help
 * menu. Nothing an investor could reach from the dark bar is unreachable from this one.
 *
 * Two things render it, and the bar itself is the same either way. On a route the shell's frame
 * wraps, `UnifiedShellFrame` lays it out as its first row and `header-maybe.tsx` renders nothing.
 * On every other route an opted-in user reaches, the header switch renders it through
 * `UnifiedTopBarOverLegacyFrame`, which pins it over the legacy page frame (PLT-6767).
 */
export interface UnifiedTopBarProps {
  onOpenNavigation?: () => void;
  /**
   * The slots another console mounting this bar replaces (the Arvo console, `views/arvo/`, which
   * sits outside `MainRoot`). Left out, each is the investor shell's own.
   */
  slots?: {
    /** Where the wordmark links. Default: the home of the organisation in view. */
    homeHref?: string;
    /**
     * A console's name after the wordmark, behind a hairline, as the API reference's bar names its
     * page (`views/api-docs/api-docs-top-bar.tsx`). Default: none.
     */
    productName?: string;
    /** The search field. Default: the global entity search; `null` leaves the slot empty. */
    search?: ReactNode;
    /**
     * The Help menu. Default: the investor product's (`UnifiedHelpMenu`); `false` leaves the slot
     * empty; a node is a console's own Help menu in the same place (the Arvo console's points at
     * its docs).
     */
    help?: boolean | ReactNode;
    /** The contextual Ask AI pill. Default: shown where the page has a chat binding. */
    askAi?: boolean;
    /** What Feedback opens. Default: `MainRoot`'s contact form, which needs `MainContext`. */
    onFeedback?: () => void;
    /** The release submenu in the account menu. Default: shown. */
    release?: boolean;
    /**
     * The "Company data from Demandbase" attribution in the account menu's footer. Default: shown;
     * a console that shows no Demandbase company data (the Arvo console) leaves it out.
     */
    companyDataAttribution?: boolean;
    /**
     * The person in the account menu when they are signed in with another identity than the
     * platform's (the Arvo console on Eevo's own). Default: the platform's signed-in user.
     */
    person?: SignedInPerson;
  };
}

export function UnifiedTopBar({ onOpenNavigation, slots = {} }: UnifiedTopBarProps) {
  // Outside `MainRoot` there is no `MainContext` (its default is `{}`), so the contact modal is
  // optional here and a console mounted elsewhere passes `slots.onFeedback`.
  const contactModal = useMainContext().contactModal as ReturnType<typeof useMainContext>['contactModal'] | undefined;
  const openFeedback =
    slots.onFeedback ??
    (() => {
      contactModal?.[1](true);
    });

  return (
    <header className={styles.bar}>
      <div className={styles.brand}>
        {/*
         * The menu button, shown only below `md`, where the rail is the drawer (PLT-6738). It
         * leads the brand slot rather than sitting in the actions cluster: it governs the region
         * on that side of the page, and a reader looking for navigation looks at the edge the
         * navigation lives on.
         *
         * The handler is optional because only the frame owns a drawer. `UnifiedTopBarOverLegacyFrame`
         * pins this bar over the legacy page frame on routes the frame does not wrap yet (PLT-6767),
         * and that path is desktop-only with the legacy rail beside it — so there is nothing for the
         * button to open, and it is not rendered rather than rendered inert.
         */}
        {onOpenNavigation && (
          <button type="button" className={styles.menuButton} aria-label="Open navigation" onClick={onOpenNavigation}>
            <MenuBars width={20} height={20} />
          </button>
        )}
        {slots.homeHref === undefined ? <InvestorLogoLink /> : <LogoLink to={slots.homeHref} />}
        {slots.productName ? (
          <span className={styles.productName}>
            <span className={styles.productNameDivider} aria-hidden="true" />
            {slots.productName}
          </span>
        ) : null}
      </div>
      <div className={styles.actions}>
        {slots.askAi === false ? null : <AskAiLink />}
        {/* Hidden below `md`, where the drawer carries it — see `.searchSlot`. */}
        {slots.search === null ? null : (
          <div className={styles.searchSlot}>{slots.search ?? <UnifiedShellSearch />}</div>
        )}
        <button
          type="button"
          className={styles.headerItem}
          aria-label="Feedback"
          title="Feedback"
          onClick={openFeedback}
        >
          <span className={styles.headerItemIcon}>
            <FeedbackIcon width={20} height={20} aria-hidden="true" />
          </span>
          <span className={styles.headerItemLabel}>Feedback</span>
        </button>
        {slots.help === false ? null : slots.help === undefined || slots.help === true ? (
          <UnifiedHelpMenu />
        ) : (
          slots.help
        )}
        <UnifiedAccountMenu
          release={slots.release !== false}
          companyDataAttribution={slots.companyDataAttribution !== false}
          person={slots.person}
        />
      </div>
    </header>
  );
}

/** The wordmark (the symbol below `lg`), linking to `to`. */
function LogoLink({ to }: { to: string }) {
  return (
    <Link to={to} className={styles.logoLink} data-testid={E2eTestElementId.LOGO} aria-label="Upright home">
      <UprightLogo className={styles.logoFull} style={{ width: '89px', height: '12.5px' }} aria-hidden="true" />
      <UprightLogoSymbolBlackWhite
        className={styles.logoSymbol}
        style={{ width: '27px', height: '16px' }}
        aria-hidden="true"
      />
    </Link>
  );
}

/** The investor shell's logo: home of the organisation in view (which needs the user's organisations). */
function InvestorLogoLink() {
  const organizationId = useNavigationOrganizationId();
  return <LogoLink to={withProfileOrganization('/', organizationId)} />;
}

/**
 * The Ask AI entry point, on the same gate and the same click contract as the dark bar's
 * and the corporate bar's (`useAskAiEntryPoint`, `useOpenAskAiPanel`): hidden without the
 * chat privilege, a plain click opens the side panel, a modifier click follows `href` to
 * the full `/chat`. Ghost pill, yellow spark — the corporate bar's treatment, because on a
 * white ground a filled yellow pill would be the loudest thing on the page.
 *
 * Contextual in the literal sense: it renders only where the page publishes a chat binding, and
 * is absent everywhere else. What it opens is an *about this page* affordance — with nothing to
 * bind to, the panel comes up over the view holding whichever conversation the app-wide runtime
 * happens to be carrying, which on a chat surface is a second chat floating over the chat the
 * user is reading. The home views, `/chat` and `/chats` all publish no binding, so the one test
 * covers them (PLT-6765). It covers the investor launcher too, whose own composer and rail row
 * already offer the chat twice over.
 *
 * Hidden rather than disabled, the call the consultant gate already makes in
 * `use-ask-ai-entry-point.ts` — a disabled control says the capability is unavailable, and here
 * it is available, one row away. That is also why the test lives here and not in
 * `useAskAiEntryPoint`: `unified-investor-navigation.tsx` gates the rail's Home row and its whole
 * Chats group on the same hook's `hidden`, so widening it would take *New chat* down with the
 * pill and leave no way into chat at all.
 */
function AskAiLink() {
  const { hidden, binding, href } = useAskAiEntryPoint('public');
  const handleClick = useOpenAskAiPanel(binding);
  if (hidden || binding === null) {
    return null;
  }
  return (
    <a
      className={styles.headerItem}
      href={href}
      onClick={handleClick}
      data-testid={E2eTestElementId.ASK_AI_LINK}
      aria-label="Ask AI"
    >
      <span className={styles.sparkMark}>
        <AiSparklesIcon width={14} height={14} aria-hidden="true" />
      </span>
      <span className={styles.headerItemLabel}>Ask AI</span>
    </a>
  );
}
